import React, { useMemo } from 'react';
import { Card, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../common/Flex';

const PerformanceAlerts = ({ orders, assignments }) => {
  const alerts = useMemo(() => {
    const alertsList = [];
    const now = new Date();

    // Verificar órdenes con problemas de presupuesto
    orders.forEach(order => {
      const orderAssignments = assignments.filter(a => a.insertionOrderId === order.id);
      const totalAssigned = orderAssignments.reduce((sum, assignment) => {
        let amount = parseFloat(assignment.montoValor) || 0;
        if (assignment.amountType === 'percentage') {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }
        return sum + amount;
      }, 0);

      const utilization = order.netInvestment > 0 ? (totalAssigned / order.netInvestment) * 100 : 0;
      const startDate = new Date(order.dateFrom);
      const endDate = new Date(order.dateTo);

      // Alerta: Sobre presupuesto
      if (utilization > 100) {
        alertsList.push({
          id: `over-budget-${order.id}`,
          type: 'danger',
          icon: 'exclamation-circle',
          title: 'Presupuesto Excedido',
          message: `${order.orderName} está ${(utilization - 100).toFixed(1)}% sobre presupuesto`,
          priority: 'high',
          timestamp: new Date()
        });
      }

      // Alerta: Cerca del límite de presupuesto
      if (utilization > 90 && utilization <= 100) {
        alertsList.push({
          id: `near-budget-${order.id}`,
          type: 'warning',
          icon: 'exclamation-triangle',
          title: 'Cerca del Límite',
          message: `${order.orderName} ha utilizado ${utilization.toFixed(1)}% del presupuesto`,
          priority: 'medium',
          timestamp: new Date()
        });
      }

      // Alerta: Orden próxima a vencer
      const daysToEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      if (daysToEnd <= 7 && daysToEnd > 0 && utilization < 80) {
        alertsList.push({
          id: `ending-soon-${order.id}`,
          type: 'info',
          icon: 'clock',
          title: 'Orden Próxima a Vencer',
          message: `${order.orderName} termina en ${daysToEnd} día(s) con solo ${utilization.toFixed(1)}% utilizado`,
          priority: 'medium',
          timestamp: new Date()
        });
      }

      // Alerta: Orden vencida sin completar
      if (endDate < now && utilization < 100) {
        alertsList.push({
          id: `expired-${order.id}`,
          type: 'danger',
          icon: 'times-circle',
          title: 'Orden Vencida',
          message: `${order.orderName} venció con ${utilization.toFixed(1)}% de utilización`,
          priority: 'high',
          timestamp: endDate
        });
      }

      // Alerta: Baja utilización en orden activa
      if (startDate <= now && endDate >= now) {
        const totalDuration = endDate.getTime() - startDate.getTime();
        const elapsedTime = now.getTime() - startDate.getTime();
        const timeProgress = Math.max(0, Math.min(1, elapsedTime / totalDuration)) * 100;
        
        if (timeProgress > 50 && utilization < timeProgress - 20) {
          alertsList.push({
            id: `low-utilization-${order.id}`,
            type: 'warning',
            icon: 'chart-line',
            title: 'Baja Utilización',
            message: `${order.orderName} está ${(timeProgress - utilization).toFixed(1)}% por debajo del ritmo esperado`,
            priority: 'medium',
            timestamp: new Date()
          });
        }
      }
    });

    // Ordenar por prioridad y fecha
    return alertsList
      .sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return b.timestamp - a.timestamp;
      })
      .slice(0, 10); // Mostrar solo las 10 alertas más importantes
  }, [orders, assignments]);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'danger': return 'exclamation-circle';
      case 'warning': return 'exclamation-triangle';
      case 'info': return 'info-circle';
      case 'success': return 'check-circle';
      default: return 'bell';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return { bg: 'danger', text: 'Alta' };
      case 'medium': return { bg: 'warning', text: 'Media' };
      case 'low': return { bg: 'info', text: 'Baja' };
      default: return { bg: 'secondary', text: 'Normal' };
    }
  };

  const alertCounts = useMemo(() => {
    return alerts.reduce((counts, alert) => {
      counts[alert.priority] = (counts[alert.priority] || 0) + 1;
      counts.total += 1;
      return counts;
    }, { high: 0, medium: 0, low: 0, total: 0 });
  }, [alerts]);

  return (
    <Card className="h-100">
      <Card.Header>
        <Flex justifyContent="between" alignItems="center">
          <h6 className="mb-0">Alertas de Performance</h6>
          <div className="d-flex align-items-center">
            {alertCounts.high > 0 && (
              <Badge bg="danger" className="me-1">{alertCounts.high}</Badge>
            )}
            <FontAwesomeIcon 
              icon="bell" 
              className={alertCounts.total > 0 ? 'text-warning' : 'text-muted'} 
            />
          </div>
        </Flex>
      </Card.Header>
      <Card.Body className="p-0">
        {alerts.length === 0 ? (
          <div className="text-center py-4">
            <FontAwesomeIcon icon="check-circle" className="text-success fs-2 mb-2" />
            <p className="text-muted mb-0">No hay alertas activas</p>
            <small className="text-muted">Todas las órdenes están funcionando correctamente</small>
          </div>
        ) : (
          <>
            {/* Resumen de alertas */}
            <div className="p-3 border-bottom bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted fw-semibold">Resumen de Alertas</small>
                <div className="d-flex gap-2">
                  {alertCounts.high > 0 && (
                    <Badge bg="danger" className="fs-11">{alertCounts.high} críticas</Badge>
                  )}
                  {alertCounts.medium > 0 && (
                    <Badge bg="warning" className="fs-11">{alertCounts.medium} importantes</Badge>
                  )}
                  {alertCounts.low > 0 && (
                    <Badge bg="info" className="fs-11">{alertCounts.low} menores</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Lista de alertas */}
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <ListGroup variant="flush">
                {alerts.map((alert) => {
                  const priorityBadge = getPriorityBadge(alert.priority);
                  return (
                    <ListGroup.Item key={alert.id} className="px-3 py-2">
                      <Flex alignItems="start">
                        <div className="me-2 mt-1">
                          <FontAwesomeIcon 
                            icon={getAlertIcon(alert.type)} 
                            className={`text-${alert.type}`} 
                          />
                        </div>
                        <div className="flex-1">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="mb-0 fs-11 fw-semibold">{alert.title}</h6>
                            <Badge bg={priorityBadge.bg} className="fs-11">
                              {priorityBadge.text}
                            </Badge>
                          </div>
                          <p className="mb-1 fs-11 text-muted">{alert.message}</p>
                          <small className="text-muted">
                            {alert.timestamp.toLocaleTimeString('es-ES', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </small>
                        </div>
                      </Flex>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default PerformanceAlerts; 